import React from "react";
import {Field, FieldProps, Formik, FormikProps} from "formik";
import {useTranslation} from "react-i18next";
import {Company} from "../../../../../types/company";
import {useDispatch, useSelector} from "react-redux";
import {State} from "../../../../../redux/store";
import {useSnack} from "../../../../../utils/useSnack";
import {Autocomplete, Grid, IconButton, TextField, TextFieldProps, Typography} from "@mui/material";
import {NewCommonForm, initialValues, ValidationSchema} from "./formik";
import {DictionaryObject, useDictionary} from "../../../../../utils/useDictionary";
import {AuctionType, ProductType} from "../../../../../utils/enums";
import {dictionaryService} from "../../../../../api/services/dictionary.service";
import {LocalizationProvider, MobileDatePicker} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import kkLocale from "date-fns/locale/kk";
import ruLocale from "date-fns/locale/ru";
import ClearIcon from "@mui/icons-material/Clear";
import {ThousandsSeparator} from "../../../../../common/NumberFormat";


export const CommonFields = ({
                                 formikRef, editingInitialState, company, lotEditing, disabled = false
                             }: {
    formikRef: React.RefObject<FormikProps<NewCommonForm>>;
    editingInitialState?: NewCommonForm,
    company: Company,
    lotEditing: boolean,
    disabled?: boolean;
}) => {
    const onSubmit = (values: NewCommonForm) => {
    };
    const {t, i18n} = useTranslation();

    const state = useSelector((state: State) => state);

    const dispatch = useDispatch();
    const {snack} = useSnack();

    const auctionType = useDictionary(
        AuctionType,
        (type) => ({value: type, label: t(`auction_type.${type}`)}),
        editingInitialState ? editingInitialState.auctionType : {
            value: AuctionType.tender,
            label: t("auction_type.tender")
        }
    );

    const productType = useDictionary(
        ProductType,
        (type) => ({value: type, label: t(`product_type.${type}`)}),
        editingInitialState
            ? editingInitialState.productType
            : {value: ProductType.product, label: t("product_type.product")}
    );

    const [intercomDictionary, setIntercomDictionary] = React.useState<Array<{ id: number, label: string }>>([]);

    const getIntercomDictionary = async () => {
        try {
            const response = await dictionaryService.getIntercom();
            if (response.status === 'success' && response.data) {
                setIntercomDictionary(response.data);
            } else {
                setIntercomDictionary([]);
            }
        } catch (err) {
            snack(t("snack.error.employees"), "error");
        }
    }
    React.useEffect(() => {
        getIntercomDictionary()
    }, [])

    React.useEffect(() => {
        formikRef.current?.setFieldValue(
            "auctionType", auctionType.selected, false);
    }, [auctionType.selected]);

    React.useEffect(() => {
        formikRef.current?.setFieldValue(
            "productType", productType.selected, false);
    }, [productType.selected]);

    const currentTime: string | null = state.public.currentTime;
    const currentDate = currentTime ? new Date(currentTime) : new Date();

    formikRef.current?.setFieldValue("companyId", company!.id)

    const investProjects = [{
        value: '1', label: '«EcoTourism Burabay» (Развитие экотуризма в Бурабайском районе)'
    }, {
        value: '2', label: '«AgroTechQyzylorda» (Тепличный комплекс в Кызылординской области)'
    }, {
        value: '3', label: 'Строительство завода аккумуляторов для электромобилей в Карагандинской области (совместно с «Volkswagen» и «Farasis Energy»)'
    }, {
        value: '4', label: 'Строительство завода по производству полипропилена в Атырау (КПО – «Казахстан Полипропилен»)'
    }, {
        value: '5', label: '«FoodHub Almaty» (Логистический центр сельхозпродукции в Алматинской области)'
    }]

    const [investProject, setInvestProject] = React.useState<{value: string, label: string} | null>(null);
    return (
        <Formik innerRef={formikRef}
                initialValues={editingInitialState || initialValues}
                validationSchema={ValidationSchema()}
                onSubmit={onSubmit}
                validateOnMount
                enableReinitialize
        >
            {(frops: FormikProps<NewCommonForm>) => (

                <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={i18n.language == "kk" ? kkLocale : ruLocale}>
                    <Grid container>
                        <Grid container columnSpacing={3} rowSpacing={1}>
                            <Grid item xs={12} sx={{mb: 1}}>
                                <Typography variant="h6">
                                    {t("cabinet.new_plan_item.sections.customer")}
                                </Typography>
                            </Grid>
                            <Field
                                type="hidden"
                                name="companyId"
                            />

                            <Grid item xs={12} sm={12} md={12}>

                                <Autocomplete
                                    disablePortal
                                    disabled={lotEditing || disabled}
                                    options={investProjects}
                                    value={investProject}
                                    onChange={( e: React.SyntheticEvent<Element, Event>,
                                                newValue: {label: string, value: string} | null) => setInvestProject(newValue!)}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    fullWidth
                                    size={"small"}
                                    renderInput={params =>
                                        <Field name={"investProject"}>
                                            {({field, form, meta}: FieldProps) => (
                                                <TextField {...params} fullWidth color="primary"
                                                           label={t("cabinet.plan.filter.investProject")}
                                                           error={!!(meta.touched && meta.error)}
                                                           helperText={meta.touched && meta.error}
                                                />
                                            )}
                                        </Field>
                                    }
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    fullWidth color="primary" type="text" size="small"
                                    value={company?.title ?? ""} name="title"
                                    label={t("auction.fields.companyTitle")}
                                    disabled={lotEditing}
                                    InputProps={{readOnly: true}}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>

                                <Autocomplete
                                    disablePortal
                                    disabled={lotEditing || disabled}
                                    options={auctionType.dictionary}
                                    value={auctionType.selected}
                                    onChange={auctionType.onSelectChange}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    fullWidth
                                    size={"small"}
                                    renderInput={params =>
                                        <Field name={"auctionType"}>
                                            {({field, form, meta}: FieldProps) => (
                                                <TextField {...params} fullWidth color="primary"
                                                           label={t("cabinet.plan.filter.auctionType")}
                                                           error={!!(meta.touched && meta.error)}
                                                           helperText={meta.touched && meta.error}
                                                />
                                            )}
                                        </Field>
                                    }
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <Autocomplete
                                    disablePortal size="small"
                                    disabled={lotEditing || disabled}
                                    options={productType.dictionary}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    value={productType.selected}
                                    onChange={productType.onSelectChange}
                                    renderInput={params =>
                                        <Field name={"productType"}>
                                            {({field, form, meta}: FieldProps) => (
                                                <TextField {...params} fullWidth color="primary"
                                                           label={t("auction.dictionary.productType")}
                                                           error={(meta.touched && meta.error) ? true : false}
                                                           helperText={meta.touched && meta.error}
                                                />
                                            )}
                                        </Field>
                                    }
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>

                                <Autocomplete
                                    disablePortal
                                    value={frops.values.deliveryType || null}
                                    disabled={disabled}
                                    options={intercomDictionary}
                                    getOptionLabel={(option) => option.label || ""}
                                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                                    fullWidth
                                    size={"small"}
                                    onChange={(event, newValue) => {
                                        // Если значение выбрано, устанавливаем объект с id и value, равным null,
                                        // иначе значение сбрасывается в null
                                        formikRef!.current!.setFieldValue(
                                            "deliveryType",
                                            newValue
                                        );
                                    }}


                                    renderInput={params =>
                                        <Field name={"deliveryType"}>
                                            {({field, form, meta}: FieldProps) => (
                                                <TextField {...params} fullWidth color="primary"
                                                           label={t("cabinet.new_lot.fields.common.delivery_type")}
                                                           error={!!(meta.touched && meta.error)}
                                                           helperText={meta.touched && meta.error}
                                                />
                                            )}
                                        </Field>
                                    }
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <MobileDatePicker
                                    minDate={currentDate}
                                    value={frops.values.deliveryDate}
                                    onChange={(newValue: any) => frops.setFieldValue("deliveryDate", newValue)}
                                    inputFormat="dd/MM/yyyy"
                                    disabled={disabled}
                                    renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => (
                                        <Field name={"deliveryDate"}>
                                            {({field, form, meta}: FieldProps) => (
                                                <TextField {...params} fullWidth size="small" color="primary"
                                                           label={t("cabinet.new_plan_item.fields.dates.delivery_date")}
                                                           error={!!(meta.touched && meta.error)}
                                                           helperText={meta.touched && meta.error}
                                                           InputLabelProps={{shrink: !!field.value}}
                                                />
                                            )}
                                        </Field>
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <MobileDatePicker
                                    minDate={currentDate}
                                    value={frops.values.auctionDate}
                                    onChange={(newValue: any) => frops.setFieldValue("auctionDate", newValue)}
                                    inputFormat="dd/MM/yyyy"
                                    disabled={disabled}
                                    renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => (
                                        <Field name={"auctionDate"}>
                                            {({field, form, meta}: FieldProps) => (
                                                <TextField {...params} fullWidth size="small" color="primary"
                                                           label={t("cabinet.new_plan_item.fields.dates.auction_date")}
                                                           error={!!(meta.touched && meta.error)}
                                                           helperText={meta.touched && meta.error}
                                                           InputLabelProps={{shrink: !!field.value}}
                                                />
                                            )}
                                        </Field>
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <Field name={'year'}>
                                    {({field, form, meta}: FieldProps) => (
                                        <TextField {...field}
                                                   disabled={lotEditing}
                                                   fullWidth color="primary" size="small"
                                                   label={t("cabinet.new_lot.financial_year")}
                                                   error={!!(meta.touched && meta.error)}
                                                   helperText={meta.touched && meta.error}
                                                   onBlur={() => {
                                                       if (+field.value === 0) form.setFieldValue(`year`, new Date().getFullYear());
                                                   }}
                                                   onChange={(e) => {
                                                       form.setFieldValue(`year`, +e.target.value)
                                                   }}
                                                   InputProps={{
                                                       endAdornment: <IconButton
                                                           onClick={() => form.setFieldValue(`year`, new Date().getFullYear())}
                                                           size="small"
                                                           color="primary"
                                                       >
                                                           <ClearIcon/>
                                                       </IconButton>
                                                   }}
                                        />
                                    )}
                                </Field>
                            </Grid>

                            <Field type="hidden" name="companyId" value={company!.id}/>

                        </Grid>
                    </Grid>
                </LocalizationProvider>
            )}
        </Formik>
    )
}